"use client"

import { useCookieBot } from "@/src/hooks/useCookieBot"
import { usePathname } from "next/navigation"
import Script from "next/script"
import "./cookieBot.css"

/**
 * The URL where the CookieBot script can be loaded from.
 */
export const COOKIEBOT_SCRIPT_SOURCE = "https://consent.cookiebot.com/uc.js"

/**
 * Selector that targets the CookieBot underlay element.
 */
const COOKIEBOT_UNDERLAY_SELECTOR = "#CybotCookiebotDialogBodyUnderlay"

type Props = {
    /**
     * CookieBot domain group ID.
     *
     * @see https://admin.cookiebot.com/domain-groups
     */
    id: string
}

/**
 * Component that sets up the CookieBot script, which handles the cookies on the website.
 */
export const CookieBotScript = ({ id }: Props) => {
    const pathname = usePathname()
    // usePathname returns a locale-prefixed path such as "/en", "/en-se" or "/",
    // so the language code is the two characters right after the leading slash.
    // "/" slices to "" and falls back to the browser language.
    const language = pathname.slice(1, 3).toLowerCase() || navigator.language

    /**
     * Remove the CookieBot underlay from the document.
     */
    const removeUnderlay = () => {
        document.querySelector(COOKIEBOT_UNDERLAY_SELECTOR)?.remove()
    }

    useCookieBot({
        onAccept() {
            // We need to remove the CookieBot underlay in order to restore interactivity
            // with the content below it once the user accepts/denies cookies.
            removeUnderlay()
        },
        onDecline() {
            removeUnderlay()
        }
    })

    return (
        <Script
            strategy="beforeInteractive"
            id="Cookiebot"
            src={COOKIEBOT_SCRIPT_SOURCE}
            data-cbid={id}
            data-culture={language}
            data-blockingmode="necessary"
            type="text/javascript"
        />
    )
}
